import { create } from "zustand";
import { logger } from "@/utils/logger";
import { CreatorOptionNode, CREATOR_DATA_TYPES } from "@/types/Creator";
import { IStore } from "@/types/interfaces";
import { AssetSelected } from "@/types/Asset";

const HISTORY_LIMIT = 10;

interface SelectionSnapshot {
  selectedOptions: Record<string, CreatorOptionNode>;
  selectedAssets: Record<string, AssetSelected>;
}

interface SelectionHistory {
  undoStack: SelectionSnapshot[];
  redoStack: SelectionSnapshot[];
}

interface SelectionState {
  selectedOptions: Record<string, CreatorOptionNode>;
  selectedAssets: Record<string, AssetSelected>;
  history: SelectionHistory;
}

interface SelectionActions extends IStore {
  // Select an item by its parent key; also builds the next menu group under the itemKey
  selectItem: (parentKey: string, optionSelected: CreatorOptionNode) => void;
  // Get selected item key for a parent key
  getSelectedItemKey: (parentKey: string) => string | undefined;
  // Get selected option for a parent key
  getSelectedOption: (parentKey: string) => CreatorOptionNode | undefined;
  // Get parent key by selected item value
  getParentKeyByItemValue: (itemKey: string) => string | undefined;
  setSelectedAsset: (key: string, value: AssetSelected) => void;
  getSelectedAsset: (key: string) => AssetSelected | undefined;
  removeSelectedAsset: (key: string) => void;
  removeSelectedItem: (parentKey: string) => void;
  // Get full current maps (avoid stale snapshot reads)
  getAllSelectedAssets: () => Record<string, AssetSelected>;
  getAllSelectedOptions: () => Record<string, CreatorOptionNode>;
  // Get all descendant selected assets recursively with parent directory info
  getDescendantSelections: (
    children: CreatorOptionNode[] | Record<string, CreatorOptionNode[]>,
    slot: string | null
  ) => Record<string, AssetSelected>;
  getSelectedAssetsByContainsKey: (key: string) => string[];

  setSelectedOptions: (data: Record<string, CreatorOptionNode>) => void;
  undo: () => AssetSelected[];
  redo: () => AssetSelected[];
  canUndo: () => boolean;
  canRedo: () => boolean;
  saveToHistory: () => void;
}

const initialState: SelectionState = {
  selectedOptions: {},
  selectedAssets: {},
  history: {
    undoStack: [],
    redoStack: []
  }
};

const cloneRecord = <T>(data: Record<string, T>): Record<string, T> => {
  const source = data || {};
  if (typeof (globalThis as any)?.structuredClone === "function") {
    return (globalThis as any).structuredClone(source);
  }
  return JSON.parse(JSON.stringify(source)) as Record<string, T>;
};

const createSnapshot = (
  selectedOptions: Record<string, CreatorOptionNode>,
  selectedAssets: Record<string, AssetSelected>
): SelectionSnapshot => ({
  selectedOptions: cloneRecord(selectedOptions),
  selectedAssets: cloneRecord(selectedAssets)
});

// Helper to save history before asset mutations
const saveToHistory = (state: SelectionState): SelectionHistory => {
  const snapshot = createSnapshot(state.selectedOptions, state.selectedAssets);

  let undoStack = [...state.history.undoStack, snapshot];
  if (undoStack.length > HISTORY_LIMIT) {
    undoStack.shift();
  }

  return {
    undoStack,
    redoStack: [] // Clear redo stack on new action
  };
};

export const useSelectionStore = create<SelectionState & SelectionActions>(
  (set: any, get: any) => ({
    ...initialState,

    initialize: async (): Promise<boolean> => {
      try {
        return true;
      } catch (error) {
        logger.error(
          "Failed to initialize SelectionStore",
          error,
          "SelectionStore"
        );
        return false;
      }
    },

    reset: () => {
      set((state: SelectionState) => {
        if (
          Object.keys(state.selectedOptions).length === 0 &&
          Object.keys(state.selectedAssets).length === 0
        ) {
          return state;
        }

        // Save history if there were assets selected
        let undoStack = state.history.undoStack;
        if (Object.keys(state.selectedAssets).length > 0) {
          undoStack = [
            ...state.history.undoStack,
            createSnapshot(state.selectedOptions, state.selectedAssets)
          ];
          if (undoStack.length > HISTORY_LIMIT) {
            undoStack.shift();
          }
        }

        return {
          ...state,
          selectedOptions: {},
          selectedAssets: {},
          history: {
            undoStack,
            redoStack: []
          }
        };
      });
    },

    getStatus: () => {
      return {
        isInitialized: true
      };
    },

    selectItem: (parentKey: string, optionSelected: CreatorOptionNode) => {
      set((state: SelectionState) => {
        if (state.selectedOptions[parentKey] === optionSelected) {
          return state;
        }

        const newSelectedItems = { ...state.selectedOptions };

        if (newSelectedItems[parentKey]) {
          // Remove previous selection recursively
          const removeSelection = (
            key: string,
            visited: Set<string> = new Set()
          ) => {
            if (visited.has(key)) {
              // Prevent infinite recursion
              return;
            }
            visited.add(key);

            if (newSelectedItems[key]) {
              const childKey = newSelectedItems[key].name;
              removeSelection(childKey, visited);
              delete newSelectedItems[key];
            }
          };
          removeSelection(parentKey);
        }

        newSelectedItems[parentKey] = optionSelected;

        return {
          ...state,
          selectedOptions: newSelectedItems
          // NO history save - only assets trigger history
        };
      });
    },

    getSelectedItemKey: (parentKey: string) => {
      const state = get();
      return state.selectedOptions[parentKey]?.name;
    },

    getSelectedOption: (parentKey: string) => {
      const state = get();
      return state.selectedOptions[parentKey];
    },

    getParentKeyByItemValue: (itemKey: string) => {
      const state = get();
      for (const [parentKey, selectedOption] of Object.entries(
        state.selectedOptions
      )) {
        if ((selectedOption as CreatorOptionNode).name === itemKey) {
          return parentKey;
        }
      }
      return undefined;
    },

    removeSelectedItem: (parentKey: string) => {
      set((state: SelectionState) => {
        const newSelectedItems = { ...state.selectedOptions };

        if (!newSelectedItems[parentKey]) {
          return state;
        }

        // Remove recursively
        const removeSelection = (key: string) => {
          if (newSelectedItems[key]) {
            removeSelection(newSelectedItems[key].name);
            delete newSelectedItems[key];
          }
        };
        removeSelection(newSelectedItems[parentKey].name);

        delete newSelectedItems[parentKey];

        return {
          ...state,
          selectedOptions: newSelectedItems
          // NO history save - only assets trigger history
        };
      });
    },

    setSelectedAsset: (key: string, value: AssetSelected) => {
      set((state: SelectionState) => {
        if (state.selectedAssets[key] === value) {
          return state;
        }

        const newSelectedAssets = { ...state.selectedAssets };
        newSelectedAssets[key] = value;

        return {
          ...state,
          selectedAssets: newSelectedAssets
        };
      });
    },

    setSelectedOptions: (data: Record<string, CreatorOptionNode>) => {
      set((state: SelectionState) => {
        if (state.selectedOptions === data) {
          return state;
        }

        return {
          ...state,
          selectedOptions: cloneRecord(data)
          // NO history save
        };
      });
    },

    getSelectedAsset: (key: string) => {
      const state = get();
      return state.selectedAssets[key];
    },

    removeSelectedAsset: (key: string) => {
      set((state: SelectionState) => {
        if (!state.selectedAssets[key]) {
          return state;
        }

        const newSelectedAssets = { ...state.selectedAssets };
        delete newSelectedAssets[key];

        return {
          ...state,
          selectedAssets: newSelectedAssets
        };
      });
    },

    undo: () => {
      set((state: SelectionState) => {
        if (state.history.undoStack.length === 0) {
          return state;
        }

        const previous =
          state.history.undoStack[state.history.undoStack.length - 1];
        const newPast = state.history.undoStack.slice(0, -1);
        const futureSnapshot = createSnapshot(
          state.selectedOptions,
          state.selectedAssets
        );
        const newFuture = [futureSnapshot, ...state.history.redoStack];
        if (newFuture.length > HISTORY_LIMIT) {
          newFuture.pop();
        }

        return {
          ...state,
          selectedOptions: cloneRecord(previous.selectedOptions),
          selectedAssets: cloneRecord(previous.selectedAssets),
          history: {
            undoStack: newPast,
            redoStack: newFuture
          }
        };
      });
      const state = get();
      return state.selectedAssets;
    },

    redo: () => {
      set((state: SelectionState) => {
        if (state.history.redoStack.length === 0) {
          return state;
        }

        const [next, ...restFuture] = state.history.redoStack;
        const pastSnapshot = createSnapshot(
          state.selectedOptions,
          state.selectedAssets
        );
        const newPast = [...state.history.undoStack, pastSnapshot];
        if (newPast.length > HISTORY_LIMIT) {
          newPast.shift();
        }

        return {
          ...state,
          selectedOptions: cloneRecord(next.selectedOptions),
          selectedAssets: cloneRecord(next.selectedAssets),
          history: {
            undoStack: newPast,
            redoStack: restFuture
          }
        };
      });
      const state = get();
      return state.selectedAssets;
    },

    saveToHistory: () => {
      set((state: SelectionState) => {
        return {
          ...state,
          history: saveToHistory(state)
        };
      });
    },

    canUndo: () => {
      const state = get();
      return state.history.undoStack.length > 0;
    },

    canRedo: () => {
      const state = get();
      return state.history.redoStack.length > 0;
    },

    getAllSelectedAssets: () => {
      const state = get();
      return state.selectedAssets;
    },

    getAllSelectedOptions: () => {
      const state = get();
      return state.selectedOptions;
    },

    getDescendantSelections: (
      children: CreatorOptionNode[] | Record<string, CreatorOptionNode[]>,
      slot: string | null
    ) => {
      const state = get();
      const foundAssets: Record<string, AssetSelected> = {};

      // Normalize input to array
      const nodes = Array.isArray(children)
        ? children
        : Object.values(children).flat();

      const getParentDirectory = (
        node: CreatorOptionNode,
        childKey: string
      ): string | null => {
        if (!node.children) {
          return null;
        }
        if (node.children[childKey]) {
          return node.children[childKey].directory || null;
        }

        return null;
      };

      // Use recursive approach for cleaner code and better performance
      const traverse = (nodes: CreatorOptionNode[], parentDir?: string) => {
        for (const node of nodes) {
          const nodeKey = node?.slot || node?.key;
          if (!nodeKey) continue;

          // Check if this node has a selected asset
          const selectedAsset = state.selectedAssets[nodeKey];
          if (selectedAsset) {
            foundAssets[nodeKey] = {
              ...selectedAsset,
              parentDirectory: parentDir || node.directory
            };
          }

          // Recursively process children
          if (node.children && Object.keys(node.children).length > 0) {
            const childNodes = Object.values(node.children).flat();
            if (childNodes.length > 0) {
              traverse(childNodes, node.directory || parentDir);
            }
          } else {
            Object.keys(state.selectedAssets).map((key) => {
              if (!foundAssets[key] && key.startsWith(nodeKey)) {
                foundAssets[key] = {
                  ...state.selectedAssets[key],
                  parentDirectory:
                    parentDir ||
                    getParentDirectory(
                      state.selectedAssets[nodeKey].assetNode,
                      state.selectedAssets[key].assetNode.parentKey
                    ) ||
                    node.directory
                };
              }
            });
          }
        }
      };
      traverse(nodes);

      if (slot) {
        for (const key in state.selectedAssets) {
          if (!key.includes(slot)) {
            if (foundAssets[key]) {
              delete foundAssets[key];
            }
          }
        }
      }

      return foundAssets;
    },

    getSelectedAssetsByContainsKey: (key: string) => {
      const state = get();
      const foundAssets: string[] = [];
      for (const slot in state.selectedAssets) {
        if (slot.includes(key)) {
          foundAssets.push(slot);
        }
      }
      return foundAssets;
    }
  })
);
